/**
 * The sport registry: the backend's `GET /sports` answer is the single source of the sport list on
 * the frontend. Every picker, label map and sport-shaped helper reads from here instead of a
 * hand-kept copy, so a sport added on the server appears everywhere at once.
 *
 * Three layers, so that module-level consumers (constants.ts and friends) always have an answer:
 *  1. `FALLBACK`   — a byte-exact flag-OFF snapshot of the endpoint, generated by the backend into
 *                    registry.fallback.json. Never edit it by hand. It IS today's sport list.
 *  2. localStorage — the last good answer from the server (key `sp_sports_registry`), so a return
 *                    visit starts from what the server said last time rather than the snapshot.
 *  3. module cache — the answer fetched since this page loaded.
 *
 * `currentRegistry()` is synchronous and returns the best of those three; `useSports()` is the
 * react-query hook for components that should re-render when the live answer lands.
 */
import {useQuery} from '@tanstack/react-query';
import {config} from '@/config/config.ts';
import fallbackJson from './registry.fallback.json';

export type ScoringType = 'sets' | 'score' | 'series' | 'field';
export type RatingMode = 'global' | 'bucket' | 'none' | 'handicap' | 'record';
export type SurfaceName =
    | 'editor'
    | 'editor_extra'
    | 'labels'
    | 'signup'
    | 'quiz'
    | 'quiz_community'
    | 'marketplace'
    | 'directory'
    | 'listing';

export interface SportTerms {
    /** The playing area: court, pitch, arena, course… */
    area: string;
    /** The unit a contest is divided into: set, game, half, quarter, map… */
    unit: string;
    /** What the score counts: points, games, goals, runs… */
    score_noun: string;
    /** The contest itself: match, race… */
    contest: string;
}

export interface SportStandings {
    win: number;
    draw: number;
    loss: number;
    tiebreaks: string[];
    /** A real rule set a new event snapshots with the flag ON; false keeps today's table (Prompt 6). */
    preset?: boolean;
    table?: 'table' | 'record';
    columns?: string[];
    extras?: Record<string, number> | unknown[];
    bonuses?: Record<string, Record<string, number>> | unknown[];
    display_divisor?: number;
    /** Volleyball's 3/2/1/0 by sets. */
    set_points?: Record<string, number>;
}

export interface SportDefinition {
    key: string;
    label: string;
    icon: string;
    emoji: string;
    enabled_without_flag: boolean;
    env_gated: boolean;
    scoring_type: ScoringType;
    scorecard: string;
    team_based: boolean;
    racquet: boolean;
    supports_draw: boolean;
    sides: string[];
    terms: SportTerms;
    defaults: Record<string, unknown> | unknown[];
    standings: SportStandings;
    /** The format group (Prompt 7): racket, team, social_target, cue_darts, mind, combat, field… */
    formats: string;
    /** The formats that group offers: league types, bracket formats, fight_card, the racquet one-offs. */
    format_list?: string[];
    rating: RatingMode;
    featured_rank: number | null;
    area_path: string | null;
    race_area: boolean;
}

/**
 * One option on a surface (picker). `key` is the stored sport key; `label` the wording that surface
 * uses ('Football / Soccer', 'Another sport'); `value` what the surface RECORDS — the quiz records
 * 'pickleball' for key 'pickelball' and its analytics group on that, so a surface must keep
 * recording `value`, not `key`.
 */
export interface SurfaceEntry {
    key: string;
    label: string;
    value: string;
}

/**
 * The caps, the Event Pass tiers, the plans and the trial length — the same config the product
 * reads (PricingConfig), carried on the registry so every sentence the site says about the gate
 * uses the product's own numbers.
 */
export interface PricingFacts {
    free_entries_cap: {tournament: number; league: number};
    event_pass_tiers: {max_entries: number | null; cents: number}[];
    annual_pass_cents: number;
    plus_monthly_cents: number;
    trial_days: number;
    golive_gate: boolean;
    /** The Event Pass waived (event_pass_waived): every tournament and league runs free, whatever its size; only Plus features are the plans'. Optional, read as off when missing. */
    event_pass_waived?: boolean;
    /** Two free finished results before the pass on an above-cap event, tournament or league (PricingConfig::freeScoreEntries); 0 asks at the first. */
    free_score_entries?: number;
    /** Payments v3: the all-in fee per currency (Free / Annual Pass rate and the Plus rate), the countries offered Express. */
    payments_v3?: {
        enabled: boolean;
        all_in_fees: Record<string, {rate: number; fixed_cents: number; plus_rate: number}>;
        fallback_currency: string;
        plus_discount_points: number;
        express_countries: string[];
        refund_keeps_fee: boolean;
    };
    /** The $2.99 Plus trial: its length, fee, whether it leads the modal, and Plus's monthly price. */
    plus_trial?: {days: number; fee_cents: number; primary: boolean; monthly_cents: number; enabled: boolean};
    /** The one-time Event Pass offer (pass_offer_enabled): the discount, how long the code lasts, the timings. */
    pass_offer?: {enabled: boolean; percent: number; hours: number; delay_hours: number; reminder_hours: number};
    /** Trial v3 (trial_v3_enabled): the one-off route's length in hours, the league route's matchdays and day cap. */
    trial_v3?: boolean;
    trial_hours?: number;
    league_trial_matchdays?: number;
    league_trial_days_cap?: number;
}

export interface SportsRegistry {
    multi_sport: boolean;
    /** Pricing facts (PricingConfig::publicFacts). Optional: an older stored registry has none. */
    pricing?: PricingFacts;
    /** The one-off Event Pass is live (entitlements enforced + one_off_path_enabled). Optional: a
     *  registry stored before the field existed reads as off. */
    one_off_path?: boolean;
    /** /event-quiz/ is switched on (event_quiz_enabled). Optional, read as off when missing. */
    event_quiz?: boolean;
    /** Football's structured results are switched on (football_v2_enabled, and the multi-sport pack
     *  with it). Optional, read as off when missing. */
    football_v2?: boolean;
    /** The organiser's League Home tab (league_home_v2_enabled). Optional, read as off when missing. */
    league_home_v2?: boolean;
    /** Share the results of a finished event (share_results_enabled). Optional, read as off when missing. */
    share_results?: boolean;
    /** Players enter their own results (player_results_enabled). Optional, read as off when missing. */
    player_results?: boolean;
    /** Payments v3: Express accounts, direct charges, the all-in fee (payments_v3_enabled). Optional, read as off when missing. */
    payments_v3?: boolean;
    /** The $2.99 Plus trial (plus_trial_enabled). Optional, read as off when missing. */
    plus_trial?: boolean;
    sports: SportDefinition[];
    surfaces: Record<SurfaceName, SurfaceEntry[]>;
    featured: string[];
}

export const FALLBACK: SportsRegistry = fallbackJson as SportsRegistry;

const STORAGE_KEY = 'sp_sports_registry';
// The server answers a repeat ask with a 304 on its ETag, so checking every minute costs little and
// a flag or registry change reaches an open page within a minute.
const STALE_MS = 60 * 1000;

const DEFAULT_TERMS: SportTerms = {area: 'court', unit: 'set', score_noun: 'points', contest: 'match'};

let cached: SportsRegistry | null = null;
let cachedAt = 0;
let storedRead = false;
let stored: SportsRegistry | null = null;

function isRegistry(candidate: unknown): candidate is SportsRegistry {
    if (!candidate || typeof candidate !== 'object') return false;
    const reg = candidate as Partial<SportsRegistry>;
    return typeof reg.multi_sport === 'boolean'
        && Array.isArray(reg.sports)
        && !!reg.surfaces && typeof reg.surfaces === 'object'
        && Array.isArray(reg.featured);
}

function readStored(): SportsRegistry | null {
    if (storedRead) return stored;
    storedRead = true;
    try {
        if (typeof localStorage === 'undefined') return null;
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        stored = isRegistry(parsed) ? parsed : null;
    } catch {
        stored = null;
    }
    return stored;
}

function writeStored(registry: SportsRegistry): void {
    try {
        if (typeof localStorage === 'undefined') return;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(registry));
    } catch {
        // Storage can be full, disabled or absent; the module cache still has the answer.
    }
}

/**
 * Set the in-memory registry directly (or clear it with null). For tests, and for any bootstrap
 * that already holds a fresh answer. Does not touch localStorage.
 */
export function primeRegistry(registry: SportsRegistry | null): void {
    cached = registry;
    cachedAt = registry ? Date.now() : 0;
}

/** The best synchronous answer: the answer fetched since the page loaded, else the stored last answer, else the snapshot. */
export function currentRegistry(): SportsRegistry {
    return cached ?? readStored() ?? FALLBACK;
}

/** Fetch the live registry. Never rejects: on any failure the fallback (snapshot) is returned. */
export async function loadSports(): Promise<SportsRegistry> {
    try {
        const response = await fetch(`${config.apiUrl}/sports`, {
            method:  'GET',
            headers: {Accept: 'application/json'},
        });
        if (!response.ok) throw new Error(`GET /sports answered ${response.status}`);
        const body: unknown = await response.json();
        if (!isRegistry(body)) throw new Error('GET /sports answered something that is not a registry');
        cached = body;
        cachedAt = Date.now();
        stored = body;
        storedRead = true;
        writeStored(body);
        return body;
    } catch {
        return cached ?? readStored() ?? FALLBACK;
    }
}

/** The registry as a react-query resource: starts from `currentRegistry()` and refreshes from the server. */
export function useSports() {
    return useQuery<SportsRegistry>({
        queryKey:             ['sports'],
        queryFn:              loadSports,
        staleTime:            STALE_MS,
        initialData:          () => currentRegistry(),
        // A snapshot or stored copy is worth showing but not worth trusting for five minutes.
        initialDataUpdatedAt: () => cachedAt,
    });
}

// ---- synchronous accessors -----------------------------------------------------------------

/**
 * A read-only map computed from the registry the page holds right now. Indexing, `in`,
 * Object.keys / entries all see the live registry; the map is rebuilt only when the registry object
 * itself changes. For module-level maps: built once at import they would freeze the snapshot a
 * first visit loads before GET /sports answers (Prompt 6).
 */
export function liveRegistryMap<T>(build: () => Record<string, T>): Record<string, T> {
    let seen: unknown = null;
    let cache: Record<string, T> = {};
    const current = (): Record<string, T> => {
        const registry = currentRegistry();
        if (registry !== seen) {
            seen = registry;
            cache = build();
        }
        return cache;
    };
    return new Proxy({} as Record<string, T>, {
        get: (_target, key) => (typeof key === 'string' ? current()[key] : undefined),
        has: (_target, key) => typeof key === 'string' && key in current(),
        ownKeys: () => Reflect.ownKeys(current()),
        getOwnPropertyDescriptor: (_target, key) => {
            const map = current();
            return typeof key === 'string' && key in map ? {value: map[key], enumerable: true, configurable: true, writable: false} : undefined;
        },
    });
}


export function sportDefinition(key: string | null | undefined, registry: SportsRegistry = currentRegistry()): SportDefinition | undefined {
    if (!key) return undefined;
    return registry.sports.find((sport) => sport.key === key);
}

const capitalise = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);

/** 'american_football' → 'American football': what a key reads like when no label is known. */
export function humaniseSportKey(key: string): string {
    return capitalise(String(key).trim().replace(/_/g, ' '));
}

/** The registry's label for a sport key, else the humanised key. */
export function sportLabel(key: string | null | undefined, registry: SportsRegistry = currentRegistry()): string {
    if (!key) return '';
    return sportDefinition(key, registry)?.label ?? humaniseSportKey(key);
}

/**
 * The options a surface offers, in the registry's order. With the multi-sport flag OFF the editor
 * gains the env-gated extras (rugby, hockey, gaming) when VITE_FEATURE_ENABLE_ADDITIONAL_SPORTS is
 * on, as it always has; with the flag ON the server already lists everything and the env flag is
 * ignored.
 */
export function sportsForSurface(surface: SurfaceName, registry: SportsRegistry = currentRegistry()): SurfaceEntry[] {
    const list = registry.surfaces[surface] ?? [];
    if (surface === 'editor' && !registry.multi_sport && config.featureEnableAdditionalSports) {
        return [...list, ...(registry.surfaces.editor_extra ?? [])];
    }
    return list;
}

export function scoringTypeOf(key: string | null | undefined, registry: SportsRegistry = currentRegistry()): ScoringType | undefined {
    return sportDefinition(key, registry)?.scoring_type;
}

/** Is the one-off Event Pass live? The logged-out event quiz reads it here, as it reads multi_sport. */
export function oneOffPathOn(registry: SportsRegistry = currentRegistry()): boolean {
    return registry.one_off_path === true;
}

/** Is the event quiz v2 (/event-quiz/) switched on? Off until the server says otherwise. */
export function eventQuizOn(registry: SportsRegistry = currentRegistry()): boolean {
    return registry.event_quiz === true;
}

/** Is "Share the results" switched on (share_results_enabled)? Off until the server says otherwise. */
export function shareResultsOn(registry: SportsRegistry = currentRegistry()): boolean {
    return registry.share_results === true;
}

/** Can players enter their own results (player_results_enabled)? Off until the server says otherwise. */
export function playerResultsOn(registry: SportsRegistry = currentRegistry()): boolean {
    return registry.player_results === true;
}

/** Is the organiser's League Home tab switched on (league_home_v2_enabled)? Off until the server says otherwise. */
export function leagueHomeV2On(registry: SportsRegistry = currentRegistry()): boolean {
    return registry.league_home_v2 === true;
}

export function isRacquetSport(key: string | null | undefined, registry: SportsRegistry = currentRegistry()): boolean {
    return sportDefinition(key, registry)?.racquet === true;
}

/** Mirrors the backend's SportType::isTeamBased: entrants are named teams, not players or pairs. */
export function isTeamBasedSport(key: string | null | undefined, registry: SportsRegistry = currentRegistry()): boolean {
    return sportDefinition(key, registry)?.team_based === true;
}

export function sportSupportsDraw(key: string | null | undefined, registry: SportsRegistry = currentRegistry()): boolean {
    return sportDefinition(key, registry)?.supports_draw === true;
}

/** One final score per side (football, basketball…) rather than sets, maps or a field result. */
export function singleScoreForSport(key: string | null | undefined, registry: SportsRegistry = currentRegistry()): boolean {
    return scoringTypeOf(key, registry) === 'score';
}

/** The sport's terms, or the racquet defaults for a sport the registry does not know. */
export function sportTerms(key: string | null | undefined, registry: SportsRegistry = currentRegistry()): SportTerms {
    return sportDefinition(key, registry)?.terms ?? DEFAULT_TERMS;
}

type TermsSubject = string | {sport_type?: string | null; venue_term?: string | null} | null | undefined;

function termsFor(subject: TermsSubject, registry: SportsRegistry): SportTerms {
    if (!subject || typeof subject === 'string') return sportTerms(subject, registry);
    const terms = sportTerms(subject.sport_type, registry);
    const chosenArea = String(subject.venue_term || '').trim().toLowerCase();
    return chosenArea ? {...terms, area: chosenArea} : terms;
}

/**
 * The terms for a sport key, or for an event ({sport_type, venue_term}) — an organiser's own
 * venue_term overrides the area. Re-renders when the live registry lands.
 */
export function useSportTerms(subject: TermsSubject): SportTerms {
    const {data} = useSports();
    return termsFor(subject, data ?? currentRegistry());
}

/**
 * The playing-area noun for an event, capitalised: the organiser's own venue_term first, else the
 * sport's area from the registry, else Court. Mirrors TournamentCourts::venueTerm on the backend so
 * every screen agrees.
 */
export function venueNounFor(sport_type: string | null | undefined, venue_term?: string | null, registry: SportsRegistry = currentRegistry()): string {
    const chosen = String(venue_term || '').trim().toLowerCase();
    if (chosen) return capitalise(chosen);
    return capitalise(sportDefinition(sport_type, registry)?.terms?.area || 'court');
}
